import { useEffect, lazy, Suspense, type ComponentType } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { getSubdomainSlug } from './lib/subdomain'
import { useTranslation } from 'react-i18next'
import { Layout } from './components/layout/Layout'
import { ChromelessLayout } from './components/layout/ChromelessLayout'
import { useSession } from './hooks/use-session'
import { usePwaBadge } from './hooks/use-pwa-badge'
import { useSplash } from './providers/SplashProvider'
import { LocaleRedirect } from './components/LocaleRedirect'
import { LocaleLayout } from './components/LocaleLayout'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { EmptyState } from './components/offers/EmptyState'
import { LocaleLink } from './components/LocaleLink'
import { Button } from './components/ui/button'
import { Waves } from 'lucide-react'

// Route-level code splitting — every page ships as its own chunk instead of
// one eager bundle. Pages use named exports, so lazy() needs the default-
// export wrapper; this helper keeps that boilerplate to one line per page.
// Mandatory pattern for any new route — see ADR-084. A route added via a
// direct static import bypasses this and silently re-grows the shared bundle.
function lazyPage<M extends Record<string, ComponentType<any>>, K extends keyof M>(
  factory: () => Promise<M>,
  name: K,
) {
  return lazy(() => factory().then((m) => ({ default: m[name] })))
}

const HomePage = lazyPage(() => import('./pages/HomePage'), 'HomePage')
const BrowsePage = lazyPage(() => import('./pages/BrowsePage'), 'BrowsePage')
const SearchResultsPage = lazyPage(() => import('./pages/SearchResultsPage'), 'SearchResultsPage')
const OfferDetailPage = lazyPage(() => import('./pages/OfferDetailPage'), 'OfferDetailPage')
const StreamPage = lazyPage(() => import('./pages/StreamPage'), 'StreamPage')
const StreamsPage = lazyPage(() => import('./pages/StreamsPage'), 'StreamsPage')
const SignInPage = lazyPage(() => import('./pages/SignInPage'), 'SignInPage')
const SignUpPage = lazyPage(() => import('./pages/SignUpPage'), 'SignUpPage')
const ForgotPasswordPage = lazyPage(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage')
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage')
const VerifyEmailPage = lazyPage(() => import('./pages/VerifyEmailPage'), 'VerifyEmailPage')
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'), 'ProfilePage')
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'), 'SettingsPage')
const DropsPage = lazyPage(() => import('./pages/DropsPage'), 'DropsPage')
const TermsPage = lazyPage(() => import('./pages/TermsPage'), 'TermsPage')
const PrivacyPage = lazyPage(() => import('./pages/PrivacyPage'), 'PrivacyPage')
const AccessibilityPage = lazyPage(() => import('./pages/AccessibilityPage'), 'AccessibilityPage')
const ClaimCheckoutPage = lazyPage(() => import('./pages/ClaimCheckoutPage'), 'ClaimCheckoutPage')
const ClaimConfirmPage = lazyPage(() => import('./pages/ClaimConfirmPage'), 'ClaimConfirmPage')
const OrderConfirmationPage = lazyPage(() => import('./pages/OrderConfirmationPage'), 'OrderConfirmationPage')
const ClaimedOffersPage = lazyPage(() => import('./pages/ClaimedOffersPage'), 'ClaimedOffersPage')
const ClaimedOfferDetailPage = lazyPage(() => import('./pages/ClaimedOfferDetailPage'), 'ClaimedOfferDetailPage')
const SavedOffersPage = lazyPage(() => import('./pages/SavedOffersPage'), 'SavedOffersPage')
const DropsBuyPage = lazyPage(() => import('./pages/DropsBuyPage'), 'DropsBuyPage')
const OfferLandingPage = lazyPage(() => import('./pages/OfferLandingPage'), 'OfferLandingPage')
const CategoriesPage = lazyPage(() => import('./pages/CategoriesPage'), 'CategoriesPage')
const CategoryOffersPage = lazyPage(() => import('./pages/CategoryOffersPage'), 'CategoryOffersPage')
const AgeConfirmPage = lazyPage(() => import('./pages/AgeConfirmPage'), 'AgeConfirmPage')
const NotificationsPage = lazyPage(() => import('./pages/NotificationsPage'), 'NotificationsPage')
const MessagesPage = lazyPage(() => import('./pages/MessagesPage'), 'MessagesPage')

/** Routes rendered outside Layout/ChromelessLayout's Outlet (so outside its
 * own Suspense boundary — see Layout.tsx/ChromelessLayout.tsx) need their
 * own boundary. No spinner per project convention — brief chunk-load gap. */
function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>
}

/**
 * Signals SplashProvider when the session query has settled (resolved or errored).
 * Splash will not hide until this fires — guarantees the app always starts
 * with a known auth state, preventing the "sign-in flash on refresh" bug.
 * Renders nothing. Mount once at the top of AppRouter.
 */
function SessionReadySignal() {
  const { isLoading } = useSession()
  const { markSessionReady } = useSplash()
  useEffect(() => {
    if (!isLoading) markSessionReady()
  }, [isLoading, markSessionReady])
  return null
}

function NotFound() {
  const { t } = useTranslation('common')
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <EmptyState
        icon={Waves}
        title={t('errors.notFound')}
        hint={t('errors.notFoundHint')}
        action={
          <Button asChild>
            <LocaleLink to="/">{t('errors.backToHome')}</LocaleLink>
          </Button>
        }
      />
    </div>
  )
}

// ─── Router ───────────────────────────────────────────────────────────────────
// All app routes live under /:locale/:lang/.
// "/" and any legacy path without a locale prefix redirect to /us/en/.
// LocaleLayout validates params and syncs i18next language with the URL.
// Layout is the persistent chrome shell — only <Outlet /> content re-renders.
//
// URL structure:
//   /us/en/                               → HomePage
//   /us/en/streams                        → StreamsPage (list)
//   /us/en/streams/:streamSlug            → StreamPage (merchant storefront)
//   /us/en/streams/:streamSlug/offers/:id → OfferDetailPage
//   /us/en/browse                         → BrowsePage (search results only)
//
// Future siblings (not nested under streams):
//   /us/en/currents/:slug/posts/:id       → Currents (publishing platform)
//   /us/en/waves/:slug/tracks/:id         → Waves (music/video)

export function AppRouter() {
  usePwaBadge()
  const subdomainSlug = getSubdomainSlug(window.location.hostname)

  // ─── Merchant subdomain: {slug}.swimflows.com ─────────────────────────────
  // Render the stream directly — no locale prefix, no full nav tree.
  // URL stays clean (no redirect to /us/en/streams/:slug).
  if (subdomainSlug) {
    return (
      <>
        <SessionReadySignal />
        <Routes>
          <Route element={<ChromelessLayout />}>
            <Route index element={<StreamPage slug={subdomainSlug} />} />
            <Route path="offers/:offerId" element={<OfferDetailPage />} />
            <Route path="sign-in" element={<SignInPage />} />
            <Route path="sign-up" element={<SignUpPage />} />
            <Route path="age-confirm" element={<AgeConfirmPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          <Route path="claim/:offerId" element={<S><ProtectedRoute><ClaimCheckoutPage /></ProtectedRoute></S>} />
          <Route path="claim/:offerId/confirm" element={<S><ProtectedRoute><ClaimConfirmPage /></ProtectedRoute></S>} />
          <Route path="order-confirmation" element={<S><ProtectedRoute><OrderConfirmationPage /></ProtectedRoute></S>} />
        </Routes>
      </>
    )
  }

  return (
    <>
      <SessionReadySignal />
    <Routes>
      {/* Root: detect locale and redirect */}
      <Route path="/" element={<LocaleRedirect />} />

      {/* All app routes under /:locale/:lang */}
      <Route path="/:locale/:lang" element={<LocaleLayout />}>
        <Route element={<Layout />}>
          {/* Public */}
          <Route index element={<HomePage />} />
          <Route path="browse" element={<BrowsePage />} />
          <Route path="search" element={<SearchResultsPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="categories/:categorySlug" element={<CategoryOffersPage />} />
          <Route path="streams" element={<StreamsPage />} />
          <Route path="streams/:streamSlug" element={<StreamPage />} />
          <Route path="streams/:streamSlug/offers/:offerId" element={<OfferDetailPage />} />

          {/* Auth */}
          <Route path="sign-in" element={<SignInPage />} />
          <Route path="sign-up" element={<SignUpPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          {/* Age gate interstitial — authenticated users who haven't confirmed age yet.
              Must NOT be inside ProtectedRoute (that would create a redirect loop). */}
          <Route path="age-confirm" element={<AgeConfirmPage />} />

          {/* Legal */}
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="accessibility" element={<AccessibilityPage />} />

          {/* Protected — requires session */}
          <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />

          {/* Account */}
          <Route path="account/claimed-offers" element={<ProtectedRoute><ClaimedOffersPage /></ProtectedRoute>} />
          <Route path="account/claimed-offers/:claimId" element={<ProtectedRoute><ClaimedOfferDetailPage /></ProtectedRoute>} />
          <Route path="account/saves" element={<ProtectedRoute><SavedOffersPage /></ProtectedRoute>} />
          <Route path="account/drops" element={<ProtectedRoute><DropsPage /></ProtectedRoute>} />
          <Route path="account/drops/buy" element={<ProtectedRoute><DropsBuyPage /></ProtectedRoute>} />

          {/* Phase 5g: dashboard — claims + activity */}

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Claim flow — full-screen takeover, no Layout chrome */}
        <Route path="claim/:offerId" element={<S><ProtectedRoute><ClaimCheckoutPage /></ProtectedRoute></S>} />
        <Route path="claim/:offerId/confirm" element={<S><ProtectedRoute><ClaimConfirmPage /></ProtectedRoute></S>} />
        <Route path="order-confirmation" element={<S><ProtectedRoute><OrderConfirmationPage /></ProtectedRoute></S>} />
      </Route>

      {/* White-label offer landing page — no locale, no chrome */}
      <Route path="/offer/:offerId" element={<S><OfferLandingPage /></S>} />

      {/* Legacy paths (no locale prefix) — redirect to detected locale */}
      <Route path="*" element={<LocaleRedirect />} />
    </Routes>
    </>
  )
}
